import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Shuffle, Check, X as XIcon, RotateCcw, Trophy, Brain, ArrowRight } from "lucide-react";
import { CATEGORY_BG, CATEGORY_LABELS, HAZARD_BG, HAZARD_LABELS } from "../lib/categories";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODES = [
  { id: "symbol", label: "Symbol → Name", desc: "Identify the element from its chemical symbol" },
  { id: "category", label: "Element → Category", desc: "Classify the element into its group" },
  { id: "hazard", label: "Element → Hazard Level", desc: "Recall the safety risk class" },
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Quiz() {
  const [elements, setElements] = useState([]);
  const [mode, setMode] = useState("symbol");
  const [deck, setDeck] = useState([]);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState({ right: 0, wrong: 0 });
  const [pick, setPick] = useState(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/elements`).then((r) => setElements(r.data));
  }, []);

  const buildDeck = useCallback(() => {
    if (elements.length === 0) return;
    setDeck(shuffle(elements).slice(0, 20));
    setIdx(0);
    setScore({ right: 0, wrong: 0 });
    setPick(null);
    setRevealed(false);
  }, [elements]);

  useEffect(() => { buildDeck(); }, [buildDeck, mode]);

  const current = deck[idx];

  const optionsFor = (el) => {
    if (!el) return [];
    let pool, correct;
    if (mode === "symbol") {
      pool = elements.map((e) => e.name);
      correct = el.name;
    } else if (mode === "category") {
      pool = Object.keys(CATEGORY_LABELS).map((k) => CATEGORY_LABELS[k]);
      correct = CATEGORY_LABELS[el.cat];
    } else {
      pool = Object.keys(HAZARD_LABELS).map((k) => HAZARD_LABELS[k]);
      correct = HAZARD_LABELS[el.hazard];
    }
    const distractors = shuffle(pool.filter((p) => p !== correct)).slice(0, 3);
    return shuffle([correct, ...distractors]);
  };

  const [options, setOptions] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (current) setOptions(optionsFor(current)); }, [current?.n, mode]);

  const answer = (opt) => {
    if (revealed) return;
    setPick(opt);
    setRevealed(true);
    const correct =
      mode === "symbol" ? current.name :
      mode === "category" ? CATEGORY_LABELS[current.cat] :
      HAZARD_LABELS[current.hazard];
    if (opt === correct) setScore((s) => ({ ...s, right: s.right + 1 }));
    else setScore((s) => ({ ...s, wrong: s.wrong + 1 }));
  };

  const next = () => {
    setPick(null);
    setRevealed(false);
    if (idx + 1 < deck.length) setIdx((i) => i + 1);
  };

  const finished = deck.length > 0 && idx >= deck.length - 1 && revealed;
  const correctAns = current && (
    mode === "symbol" ? current.name :
    mode === "category" ? CATEGORY_LABELS[current.cat] :
    HAZARD_LABELS[current.hazard]
  );

  return (
    <div className="bg-white min-h-screen" data-testid="quiz-page">
      <section className="border-b-2 border-zinc-950">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-600 mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-zinc-950" /> Study · Flashcard Quiz
          </div>
          <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl leading-[0.85] tracking-tighter uppercase">
            Test Your<br />
            <span className="bg-[#00E5FF] px-2 inline-block">Element IQ.</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-700 max-w-xl">
            20-question rounds. Pick a mode below — your score updates live as you answer.
          </p>
        </div>
      </section>

      {/* Mode picker */}
      <section className="border-b-2 border-zinc-950 bg-zinc-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 flex items-center mr-2">
            <Brain size={14} strokeWidth={2.5} className="mr-1.5" /> Mode:
          </span>
          {MODES.map((m) => (
            <button
              key={m.id}
              data-testid={`quiz-mode-${m.id}`}
              onClick={() => setMode(m.id)}
              className={`border-2 border-zinc-950 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-all ${
                mode === m.id ? "bg-zinc-950 text-white neo-shadow -translate-y-0.5" : "bg-white text-zinc-950 hover:bg-zinc-100"
              }`}
            >
              {m.label}
            </button>
          ))}
          <button
            data-testid="quiz-reshuffle"
            onClick={buildDeck}
            className="ml-auto bg-[#FFD700] border-2 border-zinc-950 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5 hover:neo-shadow hover:-translate-y-0.5 transition-all"
          >
            <Shuffle size={12} strokeWidth={2.5} /> Reshuffle
          </button>
        </div>
      </section>

      {/* Quiz */}
      <section className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {!current ? (
          <div className="font-mono text-sm text-zinc-600 text-center py-12">Loading quiz…</div>
        ) : finished && idx === deck.length - 1 ? (
          <FinalScore score={score} total={deck.length} onReplay={buildDeck} />
        ) : (
          <div className="space-y-6">
            {/* Progress */}
            <div className="flex items-center justify-between font-mono text-xs uppercase tracking-widest text-zinc-600">
              <span data-testid="quiz-progress">Question {idx + 1} / {deck.length}</span>
              <span className="flex gap-3">
                <span className="text-green-700"><Check size={12} className="inline" strokeWidth={3} /> {score.right}</span>
                <span className="text-red-600"><XIcon size={12} className="inline" strokeWidth={3} /> {score.wrong}</span>
              </span>
            </div>
            <div className="h-2 border-2 border-zinc-950 bg-white relative">
              <div
                className="absolute inset-y-0 left-0 bg-zinc-950 transition-all"
                style={{ width: `${((idx + (revealed ? 1 : 0)) / deck.length) * 100}%` }}
              />
            </div>

            {/* Card */}
            <div className="border-2 border-zinc-950 bg-white neo-shadow" data-testid="quiz-card">
              <div className={`${CATEGORY_BG[current.cat]} border-b-2 border-zinc-950 p-8 flex flex-col items-center`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2">№ {current.n}</div>
                <div className="font-heading font-black text-7xl md:text-8xl leading-none tracking-tighter">
                  {mode === "symbol" ? current.s : current.name}
                </div>
                <div className="font-mono text-xs mt-3 uppercase tracking-widest">
                  {mode === "symbol" ? "What is the name?" : mode === "category" ? "What category?" : "What hazard level?"}
                </div>
              </div>

              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                {options.map((opt) => {
                  const isCorrect = opt === correctAns;
                  const isPicked = opt === pick;
                  let cls = "bg-white text-zinc-950 hover:bg-zinc-100";
                  if (revealed && isCorrect) cls = "bg-[#00E5FF] text-zinc-950";
                  else if (revealed && isPicked) cls = "bg-[#FF003C] text-white";
                  return (
                    <button
                      key={opt}
                      data-testid={`quiz-option-${opt}`}
                      onClick={() => answer(opt)}
                      disabled={revealed}
                      className={`border-2 border-zinc-950 px-4 py-3 text-left font-mono text-sm uppercase tracking-wider transition-all ${cls} ${
                        !revealed ? "hover:neo-shadow hover:-translate-y-0.5" : ""
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>

              {revealed && (
                <div className="border-t-2 border-zinc-950 p-4 flex items-center justify-between bg-zinc-50">
                  <span className={`font-mono text-xs uppercase tracking-widest ${pick === correctAns ? "text-green-700" : "text-red-600"}`}>
                    {pick === correctAns ? "✓ Correct" : `✗ Correct: ${correctAns}`}
                  </span>
                  <button
                    onClick={next}
                    data-testid="quiz-next"
                    className="bg-zinc-950 text-white font-mono text-xs uppercase tracking-widest px-5 py-2 border-2 border-zinc-950 inline-flex items-center gap-2 hover:bg-zinc-800"
                  >
                    {idx + 1 === deck.length ? "See Score" : "Next"} <ArrowRight size={14} strokeWidth={2.5} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

const FinalScore = ({ score, total, onReplay }) => {
  const pct = Math.round((score.right / total) * 100);
  return (
    <div className="border-2 border-zinc-950 bg-white neo-shadow text-center p-10" data-testid="quiz-final">
      <Trophy size={56} strokeWidth={2.5} className="mx-auto mb-4" />
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600">Final Score</div>
      <div className="font-heading font-black text-7xl mt-2 tracking-tighter">{pct}%</div>
      <div className="font-mono text-sm mt-2">{score.right} / {total} correct</div>
      <button
        onClick={onReplay}
        data-testid="quiz-replay"
        className="mt-6 bg-zinc-950 text-white font-mono text-xs uppercase tracking-widest px-6 py-3 border-2 border-zinc-950 inline-flex items-center gap-2 hover:bg-zinc-800"
      >
        <RotateCcw size={14} strokeWidth={2.5} /> Play Again
      </button>
    </div>
  );
};
